// ============================================================
// Store — Daten-State + localStorage (React Context)
// ============================================================

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Eintrag, RouteKey, Spielhistorie } from './types';

const STORAGE_KEY = 'mein-lotto-spielanalyse.v1';

interface ToastState {
  msg: string;
  ts: number;
}

interface AppState {
  // Daten
  history: Spielhistorie | null;
  /** Letzte valide Import-Information (für Banner) */
  lastImport: { count: number; ts: number } | null;

  // UI-State
  route: RouteKey;
  setRoute: (r: RouteKey) => void;

  periodMode: 'gesamt' | 'curMonth' | 'lastMonth' | 'curYear' | 'lastYear' | 'd30' | 'd90' | 'd365' | 'custom';
  setPeriodMode: (m: AppState['periodMode']) => void;
  customFrom: string;
  customTo: string;
  setCustomFrom: (s: string) => void;
  setCustomTo: (s: string) => void;

  compare: boolean;
  setCompare: (b: boolean) => void;

  /** Detail-Drawer */
  detailId: string | null;
  setDetailId: (id: string | null) => void;

  /** Toast */
  toast: ToastState | null;
  showToast: (msg: string) => void;

  /** Daten-Mutationen */
  replaceHistory: (h: Spielhistorie, count: number) => void;
  clearHistory: () => void;
}

const Ctx = createContext<AppState | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [history, setHistory] = useState<Spielhistorie | null>(null);
  const [lastImport, setLastImport] = useState<AppState['lastImport']>(null);

  const [route, setRouteRaw] = useState<RouteKey>(() => {
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem('mein-lotto.route') : null;
    return (stored as RouteKey) || 'uebersicht';
  });
  const setRoute = useCallback((r: RouteKey) => setRouteRaw(r), []);

  const [periodMode, setPeriodModeRaw] = useState<AppState['periodMode']>('gesamt');
  const setPeriodMode = useCallback((m: AppState['periodMode']) => setPeriodModeRaw(m), []);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const [compare, setCompareRaw] = useState(false);
  const setCompare = useCallback((b: boolean) => setCompareRaw(b), []);

  const [detailId, setDetailId] = useState<string | null>(null);

  const [toast, setToast] = useState<ToastState | null>(null);
  const toastTimer = useRef<number | null>(null);
  const showToast = useCallback((msg: string) => {
    setToast({ msg, ts: Date.now() });
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 3200);
  }, []);

  // ---- Persistenz ----
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && parsed.history) {
          setHistory(parsed.history as Spielhistorie);
        }
      }
    } catch {
      // bewusst still — kaputter localStorage-Stand → leerer State
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (history) {
      try {
        window.localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ history }),
        );
      } catch {
        // Quota überschritten o. ä. → still ignorieren
      }
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }, [history]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('mein-lotto.route', route);
  }, [route]);

  // ---- Aktionen ----
  const replaceHistory = useCallback((h: Spielhistorie, count: number) => {
    setHistory(h);
    setLastImport({ count, ts: Date.now() });
    showToast(`Datensatz erfolgreich ersetzt · ${count} Einträge`);
    setRoute('uebersicht');
  }, [showToast, setRoute]);

  const clearHistory = useCallback(() => {
    setHistory(null);
    setLastImport(null);
    setRoute('datenimport');
  }, [setRoute]);

  const value = useMemo<AppState>(
    () => ({
      history,
      lastImport,
      route,
      setRoute,
      periodMode,
      setPeriodMode,
      customFrom,
      customTo,
      setCustomFrom,
      setCustomTo,
      compare,
      setCompare,
      detailId,
      setDetailId,
      toast,
      showToast,
      replaceHistory,
      clearHistory,
    }),
    [
      history,
      lastImport,
      route,
      setRoute,
      periodMode,
      setPeriodMode,
      customFrom,
      customTo,
      compare,
      setCompare,
      detailId,
      toast,
      showToast,
      replaceHistory,
      clearHistory,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp muss innerhalb von <AppProvider> verwendet werden');
  return v;
}

export function useEntries(): Eintrag[] {
  const { history } = useApp();
  return history?.eintraege ?? [];
}
